import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { BookOpen, Upload, CheckCircle, Clock, Award, Paperclip, X } from 'lucide-react';
import FileUpload from './FileUpload';

/**
 * StudentHomeworkSection — list assignments visible to student + submit dialog.
 */
export const StudentHomeworkSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);   // assignment currently being submitted
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  // Files uploaded to Emergent Object Storage during this dialog session
  const [cloudFiles, setCloudFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/student/homework');
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load homework');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openSubmit = (a) => {
    setActive(a);
    setText(a.my_submission?.text_answer || '');
    setFiles([]);
    setCloudFiles([]);
  };

  const onFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!active) return;
    if (!text.trim() && files.length === 0 && cloudFiles.length === 0) {
      return toast.error('Add a text answer or upload a file');
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('text_answer', text);
      files.forEach(f => fd.append('files', f));
      if (cloudFiles.length > 0) fd.append('attachment_ids', cloudFiles.map(f => f.id).join(','));
      await api.post(`/student/homework/${active.id}/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Homework submitted');
      setActive(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
    setSubmitting(false);
  };

  const statusBadge = (a) => {
    const sub = a.my_submission;
    if (!sub) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-slate-200 text-slate-600"><Clock className="w-3 h-3 inline mr-1" />Not started</span>;
    if (sub.status === 'graded') return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700"><Award className="w-3 h-3 inline mr-1" />Graded: {sub.score}/{a.max_score}</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700"><CheckCircle className="w-3 h-3 inline mr-1" />Submitted</span>;
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-blue-600" /> My Homework
        </h2>
        <p className="text-slate-500 font-semibold text-sm">Submit assignments and view feedback from your teachers</p>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-12">Loading…</div>
      ) : items.length === 0 ? (
        <div className="nb-card text-center py-12">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-semibold">No assignments yet</p>
          <p className="text-xs text-slate-400 mt-1">Your teacher hasn't published any homework. Check back soon!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(a => (
            <div key={a.id} className="nb-card p-5" data-testid={`student-hw-card-${a.id}`}>
              <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">{a.topic_title}</span>
                {statusBadge(a)}
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">{a.title}</h3>
              <div className="text-xs text-slate-500 line-clamp-3 mb-3" dangerouslySetInnerHTML={{ __html: a.instructions_html || '<i>No instructions</i>' }} />
              <div className="flex items-center gap-2 text-xs text-slate-600 mb-3">
                <Award className="w-3.5 h-3.5 text-amber-500" /> {a.max_score} pts
                {a.due_offset_days != null && <span className="text-slate-400">• Due +{a.due_offset_days}d after class</span>}
              </div>
              {a.my_submission?.status === 'graded' && a.my_submission?.feedback && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs text-emerald-800 mb-3">
                  <strong>Teacher's feedback:</strong> {a.my_submission.feedback}
                </div>
              )}
              <Button onClick={() => openSubmit(a)} className="w-full bg-blue-600 hover:bg-blue-700 font-bold text-xs"
                data-testid={`submit-hw-btn-${a.id}`}>
                {a.my_submission ? 'Re-submit' : 'Start Submission'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Submit dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="bg-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold">Submit — {active?.title}</DialogTitle>
            <DialogDescription>Write your answer, upload files, or both.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Textarea value={text} onChange={e => setText(e.target.value)}
              placeholder="Type your answer or notes here…"
              className="min-h-[160px]" data-testid="student-hw-text" />
            <div>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full" data-testid="student-hw-attach">
                <Paperclip className="w-4 h-4 mr-1" /> Attach files (PDF, images, ZIP — max 25 MB each)
              </Button>
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFileChange} />
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-100 rounded px-2 py-1">
                      <span className="truncate flex-1">{f.name} <span className="text-slate-400">({Math.round(f.size / 1024)} KB)</span></span>
                      <button onClick={() => removeFile(i)} className="text-red-500 ml-2"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Emergent Object Storage — larger attachments (up to 25 MB each) */}
            <div>
              <p className="text-xs font-bold text-slate-600 mb-1.5">…or drop attachments here (goes to secure cloud storage):</p>
              <FileUpload
                variant="dropzone"
                context="homework"
                refId={active?.id}
                multiple
                maxSizeMB={25}
                accept="image/*,application/pdf,application/zip,text/*,.py,.js,.html,.css"
                onUploaded={f => setCloudFiles(prev => [...prev, f])}
                label="Drop files here"
                dataTestid="hw-cloud-upload"
              />
              {cloudFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {cloudFiles.map((f, i) => (
                    <div key={f.id} className="flex items-center justify-between text-xs bg-indigo-50 border border-indigo-100 rounded px-2 py-1" data-testid={`hw-cloud-file-${f.id}`}>
                      <span className="truncate flex-1 font-semibold">☁️ {f.filename}</span>
                      <button onClick={() => setCloudFiles(prev => prev.filter(x => x.id !== f.id))} className="text-red-500 ml-2"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 font-bold"
              data-testid="confirm-submit-hw">
              <Upload className="w-4 h-4 mr-1" /> {submitting ? 'Submitting…' : 'Submit Homework'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentHomeworkSection;
